import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Home,
  TrendingUp,
  TrendingDown,
  Star,
  Equalizer,
  DonutLarge,
  ArrowBack,
} from "@mui/icons-material";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getEventSongsData } from "../data/songsData";
import { votingService } from "../services/votingService";

const VoteSuccessPage = () => {
  const navigate = useNavigate();
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const { songs, categories, nights } = getEventSongsData(eventId);
  const { user } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const currentNight = parseInt(searchParams.get("night") || "1", 10);
  const isPartial = searchParams.get("partial") === "true";
  const currentNightConfig = nights[currentNight] || nights[1];

  const { data: userVotesData, isLoading } = useQuery({
    queryKey: ["userVotes", eventId, user?.uid],
    queryFn: () => votingService.getUserVotes(user?.uid, eventId),
    enabled: !!user?.uid,
  });

  const userVotes = useMemo(() => userVotesData?.votes || {}, [userVotesData]);

  const handleGoHome = () => {
    navigate(`/${eventId}/results`);
  };

  const processedSongs = useMemo(() => {
    if (!userVotes) return [];

    return songs
      .filter(currentNightConfig.filter)
      .map((song) => {
        let songTotal = 0;
        const songVotes = {};

        categories.forEach((cat) => {
          // Check if category is valid for this night
          if (cat.key === "canzone_cover" && !currentNightConfig.isCoverNight) {
            return;
          }

          let voteKey;
          if (cat.key === "canzone") {
            voteKey = `${song.key}_${cat.key}`;
          } else {
            voteKey = `${song.key}_${currentNight}_${cat.key}`;
          }

          const val = userVotes[voteKey] || 0;
          songVotes[cat.key] = val;
          songTotal += val;
        });

        return {
          ...song,
          myTotalScore: songTotal,
          myVotes: songVotes,
        };
      })
      .sort((a, b) => b.myTotalScore - a.myTotalScore);
  }, [userVotes, currentNight, currentNightConfig, songs, categories]);

  const stats = useMemo(() => {
    if (processedSongs.length === 0) return null;

    let totalScore = 0;
    let totalVotesCount = 0;
    const categoryTotals = {};
    const categoryCounts = {};

    categories.forEach((cat) => {
      if (cat.key === "canzone_cover" && !currentNightConfig.isCoverNight)
        return;
      categoryTotals[cat.key] = 0;
      categoryCounts[cat.key] = 0;
    });

    processedSongs.forEach((song) => {
      totalScore += song.myTotalScore;

      Object.keys(song.myVotes).forEach((catKey) => {
        if (categoryTotals[catKey] !== undefined) {
          categoryTotals[catKey] += song.myVotes[catKey];
          categoryCounts[catKey]++;
          totalVotesCount++;
        }
      });
    });

    const averageVote =
      totalVotesCount > 0 ? (totalScore / totalVotesCount).toFixed(1) : 0;

    // Find favorite and strictest category
    let maxAvg = -1;
    let minAvg = 11;
    let favCat = null;
    let strictCat = null;

    Object.keys(categoryTotals).forEach((catKey) => {
      const count = categoryCounts[catKey];
      if (count > 0) {
        const avg = categoryTotals[catKey] / count;
        if (avg > maxAvg) {
          maxAvg = avg;
          favCat = catKey;
        }
        if (avg < minAvg) {
          minAvg = avg;
          strictCat = catKey;
        }
      }
    });

    const favCatObj = categories.find((c) => c.key === favCat);
    const strictCatObj = categories.find((c) => c.key === strictCat);

    return {
      totalScore,
      averageVote,
      favCatLabel: favCatObj?.label,
      favCatColor: favCatObj?.color,
      strictCatLabel: strictCatObj?.label,
      strictCatColor: strictCatObj?.color,
      favCatAvg: maxAvg.toFixed(1),
      strictCatAvg: minAvg.toFixed(1),
    };
  }, [processedSongs, currentNightConfig, categories]);

  const categoryTop3 = useMemo(() => {
    if (processedSongs.length === 0) return [];

    const result = [];
    categories.forEach((cat) => {
      if (cat.key === "canzone_cover" && !currentNightConfig.isCoverNight)
        return;

      const top3 = [...processedSongs]
        .sort((a, b) => {
          const voteA = a.myVotes[cat.key] || 0;
          const voteB = b.myVotes[cat.key] || 0;
          return voteB - voteA; // Descending order
        })
        .slice(0, 3)
        .map((song) => ({
          ...song,
          vote: song.myVotes[cat.key] || 0,
        }));

      result.push({
        category: cat,
        songs: top3,
      });
    });

    return result;
  }, [processedSongs, currentNightConfig, categories]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 1000, mx: "auto", mt: 4 }}>
      <Box textAlign="center" mb={4}>
        {isPartial ? (
          <DonutLarge color="warning" sx={{ fontSize: 60, mb: 2 }} />
        ) : (
          <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
        )}
        <Typography variant="h4" gutterBottom>
          {isPartial ? "Riepilogo Parziale" : "Voti Inviati con Successo!"}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isPartial
            ? "Ecco i voti che hai inserito finora. Ricordati di completare e inviare!"
            : `Grazie per aver partecipato alla votazione di ${eventId === "eurovision" ? "Eurovision 2026" : "Sanremo 2026"}!`}
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: "bold" }}>
          Riepilogo: {currentNightConfig.label}
        </Typography>
      </Box>

      {/* Stats Section */}
      {stats && (
        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ p: 2, textAlign: "center", height: "100%" }}
              >
                <Equalizer color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Media Voti
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.averageVote}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ p: 2, textAlign: "center", height: "100%" }}
              >
                <TrendingUp color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Categoria Preferita
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: stats.favCatColor || "inherit" }}
                >
                  {stats.favCatLabel || "-"}
                </Typography>
                <Typography variant="caption">
                  Media: {stats.favCatAvg}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ p: 2, textAlign: "center", height: "100%" }}
              >
                <TrendingDown color="error" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Categoria più Severa
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: stats.strictCatColor || "inherit" }}
                >
                  {stats.strictCatLabel || "-"}
                </Typography>
                <Typography variant="caption">
                  Media: {stats.strictCatAvg}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{ p: 2, textAlign: "center", height: "100%" }}
              >
                <Star color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Punti Totali Assegnati
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalScore}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      <Divider sx={{ mb: 4 }} />

      {/* Top 3 per Category */}
      {categoryTop3.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            I tuoi Top 3 per Categoria:
          </Typography>
          <Grid container spacing={2}>
            {categoryTop3.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.category.key}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      bgcolor: item.category.color,
                      color: "white",
                      p: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.category.label}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                    {item.songs.map((song, idx) => (
                      <Box
                        key={song.key}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                        sx={{
                          borderBottom: idx < 2 ? "1px solid #eee" : "none",
                          pb: idx < 2 ? 1 : 0,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              bgcolor:
                                idx === 0
                                  ? "gold"
                                  : idx === 1
                                    ? "silver"
                                    : "#cd7f32",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              fontWeight: "bold",
                            }}
                          >
                            {idx + 1}
                          </Box>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {song.artist}
                          </Typography>
                        </Box>
                        <Chip
                          label={song.vote}
                          size="small"
                          sx={{
                            bgcolor: item.category.color,
                            color: "white",
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h6" gutterBottom>
        La tua classifica:
      </Typography>
      <Grid container spacing={2}>
        {processedSongs.map((song, index) => {
          return (
            <Grid item xs={12} sm={6} md={4} key={song.key}>
              <Card
                variant={index < 3 ? "elevation" : "outlined"}
                elevation={index < 3 ? 4 : 1}
                sx={{
                  position: "relative",
                  border: index < 3 ? "2px solid" : undefined,
                  borderColor:
                    index === 0
                      ? "gold"
                      : index === 1
                        ? "silver"
                        : index === 2
                          ? "#cd7f32"
                          : "transparent",
                }}
              >
                {index < 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      bgcolor:
                        index === 0
                          ? "gold"
                          : index === 1
                            ? "silver"
                            : "#cd7f32",
                      color: "white",
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </Box>
                )}
                <CardContent>
                  <Typography variant="subtitle2" color="primary">
                    #{song.order} - {song.country}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {song.artist} - {song.title}
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    Totale: {song.myTotalScore}
                  </Typography>
                  <Box mt={1}>
                    {categories.map((cat) => {
                      if (
                        cat.key === "canzone_cover" &&
                        !currentNightConfig.isCoverNight
                      ) {
                        return null;
                      }

                      const voteValue = song.myVotes[cat.key] || 0;

                      return (
                        <Chip
                          key={cat.key}
                          label={`${cat.label}: ${voteValue}`}
                          size="small"
                          sx={{
                            mr: 0.5,
                            mb: 0.5,
                            bgcolor: cat.color,
                            color: "white",
                          }}
                        />
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box
        mt={3}
        textAlign="center"
        display="flex"
        justifyContent="center"
        gap={2}
      >
        {isPartial && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/${eventId}/voting?night=${currentNight}`)}
            size="large"
          >
            Torna al Voto
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<Home />}
          onClick={handleGoHome}
          size="large"
        >
          Vai ai Risultati
        </Button>
      </Box>
    </Paper>
  );
};

export default VoteSuccessPage;
